import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {Observable, of} from 'rxjs';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import {
  InteractiveDataAnalysisRequest
} from '@models/pages/interactive-data-analysis/interactive-data-analysis-request';
import {SignalField} from '@models/pages/interactive-data-analysis/interactive-data-analysis-signals';
import {TranslateService} from '@ngx-translate/core';
import {AggregationType} from '@models/pages/interactive-data-analysis/aggregation-type';
import {IntervalTypes} from '@constants/pages/interactive-data-analysis/interval-types';
import {InteractiveDataAnalysisConfig} from '@models/pages/interactive-data-analysis/interactive-data-analysis-config';
import {
  Axis,
  InteractiveDataAnalysisSignalChartProperties
} from '@models/pages/interactive-data-analysis/interactive-data-analysis-signal-chart-properties';
import {StorageService} from '@services/storage/storage.service';
import {shareReplay} from 'rxjs/operators';
import {ChartsHelperService} from '@services/charts/helper/charts-helper.service';
import {ChartSignalEntry} from '@models/pages/user-defined-page/chart-signal-entry';
import {InteractiveDataAnalysisProperty} from '@models/pages/interactive-data-analysis/interactive-data-analysis-property';
import {AggregationTypesService} from './aggregation-types.service';
import {AggregationTypes} from '@constants/pages/interactive-data-analysis/aggregation-types';
import {
  AggregatedSignalData,
  DataVisualizationValues,
  InteractiveDataAnalysisResponse,
  ParsedSignalData
} from '@models/pages/interactive-data-analysis/interactive-data-analysis-data';

@Injectable({
  providedIn: 'root'
})
export class InteractiveDataAnalysisService {

  public readonly NO_STRING_VALUE: string = '';
  private LIST_SAVED_SIGNALS_URL_SUFFIX = '/api/web/admin/machine/:machineId/listSavedSignals';
  private GET_DATA_VISUALIZATION_URL_SUFFIX = '/api/web/tenant/:tenantId/machine/:machineId/cockpit/dataVisualization';
  private GET_STRING_DATA_VISUALIZATION_URL_SUFFIX = '/api/web/tenant/:tenantId/machine/:machineId/cockpit/dataVisualization/stringValue';
  private GET_DATA_VISUALIZATION_CONFIGURATION_URL_SUFFIX = '/api/web/base/:machineId/dataVisualizationPage';
  private UPDATE_DATA_VISUALIZATION_CONFIGURATION_URL_SUFFIX = '/api/web/admin/machine/:machineId/dataVisualization';

  private readonly BASE_URL: string;
  private readonly INTERVAL_OF_1: number = 1;
  private readonly INTERVAL_OF_10: number = 10;
  private readonly INTERVAL_OF_60: number = 60;
  private readonly INTERVAL_OF_900: number = 900;
  private readonly INTERVAL_OF_3600: number = 3600;
  private readonly HOURS: number = 12;
  private readonly DAY: number = 24;
  private readonly DAYS_10: number = 10 * 24;
  private readonly DAYS_120: number = 120 * 24;

  private machineFullSignalsList: Map<string, any> = new Map();

  constructor(
    private http: HttpClient,
    private apiLocation: ApiLocationService,
    private translateService: TranslateService,
    private storageService: StorageService,
    private aggregationTypesService: AggregationTypesService
  ) {
    this.BASE_URL = this.apiLocation.getLocation();
    this.NO_STRING_VALUE = this.translateService.instant('- No string -');
  }

  public dropCache(): void {
    this.machineFullSignalsList = new Map();
  }

  private static getAggregationInterval(customBaseUnit: string) {
    switch (customBaseUnit) {
      case AggregationTypes.SECONDS10:
        return -1;
      case AggregationTypes.MINUTE:
        return 60;
      case AggregationTypes.MINUTES15:
        return 15 * 60;
      case AggregationTypes.HOUR:
        return 60 * 60;
      case AggregationTypes.DAY:
        return 24 * 60 * 60;
      case AggregationTypes.MONTH:
        return 30 * 24 * 60 * 60;
      default:
        return -1;
    }
  }

  private static getAxisType(signal: SignalField, signalsOrderedByConfigurationsType: SignalField[]): Axis {
    const index = signalsOrderedByConfigurationsType.indexOf(signal);
    if (index % 2 === 0) {
      return Axis.LEFT;
    }
    return Axis.RIGHT;
  }

  public listAllPossibleSignalsForMachine(machineId: string): Observable<any> {

    let url: string = this.BASE_URL + this.LIST_SAVED_SIGNALS_URL_SUFFIX;
    url = url.replace(':machineId', machineId);

    const machineSignalsFromCache = this.machineFullSignalsList.get(url);
    if (machineSignalsFromCache) {
      return of(machineSignalsFromCache);
    }

    const response = this.http.get(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true
    }));
    response.subscribe((result) => {
      this.machineFullSignalsList.set(url, result);
    });
    return response;
  }

  public getDataVisualizationValues(machineId: number, organizationId: number, dataVisualizationRequest: InteractiveDataAnalysisRequest): Observable<InteractiveDataAnalysisResponse> {
    let url: string = this.BASE_URL + this.GET_DATA_VISUALIZATION_URL_SUFFIX;
    url = url.replace(':tenantId', String(organizationId)).replace(':machineId', String(machineId));
    return this.http.post(url, dataVisualizationRequest, HTTPS_REQUEST_OPTIONS) as Observable<InteractiveDataAnalysisResponse>;
  }

  public getDataVisualizationStringValues(machineId: number, organizationId: number, dataVisualizationRequest: InteractiveDataAnalysisRequest): Observable<any> {
    let url: string = this.BASE_URL + this.GET_STRING_DATA_VISUALIZATION_URL_SUFFIX;
    url = url.replace(':tenantId', String(organizationId)).replace(':machineId', String(machineId));
    return this.http.post(url, dataVisualizationRequest, HTTPS_REQUEST_OPTIONS);
  }

  public sortSignalList(signals: SignalField[]): SignalField[] {
    return signals.sort(function (a, b) {
      if (a.id === '-1') {
        return -1;
      }
      if (b.id === '-1') {
        return 1;
      }
      if (!a.displayedText && !b.displayedText) {
        return 0;
      }
      if (!a.displayedText) {
        return 1;
      }
      if (!b.displayedText) {
        return -1;
      }
      if (a.machineSignal && b.machineSignal !== a.machineSignal) {
        return -1;
      }
      if (b.machineSignal && b.machineSignal !== a.machineSignal) {
        return 1;
      }
      const displayedTextA = a.displayedText.toUpperCase();
      const displayedTextB = b.displayedText.toUpperCase();
      return (displayedTextA < displayedTextB) ? -1 : (displayedTextA > displayedTextB) ? 1 : 0;
    });
  }

  public getIntervalTypes() {
    return [
      {id: IntervalTypes.CURRENT_DAY, name: 'Current Day'},
      {id: IntervalTypes.YESTERDAY, name: 'Yesterday'},
      {id: IntervalTypes.LAST_7_DAYS, name: 'Last 7 days'},
      {id: IntervalTypes.LAST_30_DAYS, name: 'Last 30 days'},
      {id: IntervalTypes.CURRENT_MONTH, name: 'Current Month'},
      {id: IntervalTypes.CURRENT_YEAR, name: 'Current Year'},
      {id: IntervalTypes.CUSTOM, name: 'Custom'}
    ];
  }

  public getConfiguration(machineId: string): Observable<any> {
    let url: string = this.BASE_URL + this.GET_DATA_VISUALIZATION_CONFIGURATION_URL_SUFFIX;
    url = url.replace(':machineId', machineId);
    return this.http.get(url, HTTPS_REQUEST_OPTIONS);
  }

  public getPossibleUnits(): Map<string, AggregationType[]> {
    const map: Map<string, AggregationType[]> = new Map<string, AggregationType[]>();
    map.set(IntervalTypes.CURRENT_DAY, [this.aggregationTypesService.TYPE_SECONDS10, this.aggregationTypesService.TYPE_MINUTE, this.aggregationTypesService.TYPE_MINUTES15_WITH_DEFAULT, this.aggregationTypesService.TYPE_HOUR]);
    map.set(IntervalTypes.YESTERDAY, [this.aggregationTypesService.TYPE_SECONDS10_WITH_DEFAULT, this.aggregationTypesService.TYPE_MINUTE, this.aggregationTypesService.TYPE_MINUTES15, this.aggregationTypesService.TYPE_HOUR_WITH_DEFAULT]);
    map.set(IntervalTypes.LAST_7_DAYS, [this.aggregationTypesService.TYPE_MINUTE_WITH_MONTH_DEFAULT, this.aggregationTypesService.TYPE_MINUTES15_WITH_MONTH, this.aggregationTypesService.TYPE_HOUR_WITH_MONTH, this.aggregationTypesService.TYPE_DAY_WITH_MONTH]);
    map.set(IntervalTypes.LAST_30_DAYS, [this.aggregationTypesService.TYPE_MINUTES15_WITH_MONTH_DEFAULT, this.aggregationTypesService.TYPE_HOUR_WITH_MONTH, this.aggregationTypesService.TYPE_DAY_WITH_MONTH]);
    map.set(IntervalTypes.CURRENT_MONTH, [this.aggregationTypesService.TYPE_MINUTES15_WITH_DAYS_DEFAULT, this.aggregationTypesService.TYPE_HOUR_WITH_DAYS, this.aggregationTypesService.TYPE_DAY]);
    map.set(IntervalTypes.CURRENT_YEAR, [this.aggregationTypesService.TYPE_HOUR_WITH_MONTH_DEFAULT, this.aggregationTypesService.TYPE_MONTH, this.aggregationTypesService.TYPE_DAY_WITH_MONTH_DEFAULT]);
    map.set(IntervalTypes.CUSTOM_HOURS, [this.aggregationTypesService.TYPE_SECOND, this.aggregationTypesService.TYPE_HOUR, this.aggregationTypesService.TYPE_MINUTE]);
    map.set(IntervalTypes.CUSTOM_24_HOURS, [this.aggregationTypesService.TYPE_SECONDS10_WITH_DEFAULT, this.aggregationTypesService.TYPE_HOUR_WITH_DEFAULT, this.aggregationTypesService.TYPE_MINUTE]);
    map.set(IntervalTypes.CUSTOM_DAY, [this.aggregationTypesService.TYPE_SECONDS10_WITH_MONTH_DEFAULT, this.aggregationTypesService.TYPE_MINUTE_WITH_MONTH, this.aggregationTypesService.TYPE_MINUTES15_WITH_MONTH, this.aggregationTypesService.TYPE_HOUR_WITH_MONTH]);
    map.set(IntervalTypes.CUSTOM_DAYS, [this.aggregationTypesService.TYPE_MINUTE_WITH_MONTH_DEFAULT, this.aggregationTypesService.TYPE_MINUTES15_WITH_MONTH, this.aggregationTypesService.TYPE_HOUR_WITH_MONTH, this.aggregationTypesService.TYPE_DAY_WITH_MONTH]);
    map.set(IntervalTypes.CUSTOM_MONTHS, [this.aggregationTypesService.TYPE_MINUTES15_WITH_MONTH_DEFAULT, this.aggregationTypesService.TYPE_HOUR_WITH_MONTH, this.aggregationTypesService.TYPE_DAY_WITH_MONTH]);
    map.set(IntervalTypes.CUSTOM_YEAR, [this.aggregationTypesService.TYPE_HOUR_WITH_MONTH_MINUTES_ZERO_DEFAULT, this.aggregationTypesService.TYPE_MONTH, this.aggregationTypesService.TYPE_DAY_WITH_MONTH_DEFAULT]);
    map.set(IntervalTypes.CUSTOM, []);
    return map;
  }

  public updateConfiguration(machineId: string, interactiveDataAnalysisConfig: InteractiveDataAnalysisConfig): Observable<any> {
    let url: string = this.BASE_URL + this.UPDATE_DATA_VISUALIZATION_CONFIGURATION_URL_SUFFIX;
    url = url.replace(':machineId', machineId);
    return this.http.post(url, this.parseConfig(interactiveDataAnalysisConfig), HTTPS_REQUEST_OPTIONS);
  }

  private parseConfig(config: InteractiveDataAnalysisConfig): InteractiveDataAnalysisConfig {
    const parsedObject = {
      chartId: config.chartId,
      parentTab: config.parentTab,
      label: config.label,
      properties: {},
    };

    for (const key of config.properties.keys()) {
      parsedObject.properties[key] = config.properties.get(key);
    }

    return <InteractiveDataAnalysisConfig>parsedObject;
  }

  public getDefaultDisplayInterval(id: string, aggregationUnits: Map<string, AggregationType[]>): string {
    const aggregationTypes = aggregationUnits.get(id);
    if (aggregationTypes === undefined) {
      return '';
    }
    for (const aggregationUnit of aggregationTypes) {
      if (aggregationUnit.default && aggregationUnit.default === 'true') {
        return aggregationUnit.name;
      }
    }
    return '';
  }

  public getNoStringSignal() {
    const noStringSignal = {
      id: '-1',
      name: ChartsHelperService.NO_STRING,
      label: this.NO_STRING_VALUE,
      displayedText: this.NO_STRING_VALUE,
    };
    return noStringSignal as SignalField;
  }

  public getRequestSignals(selectedSignals: string[], signalTypes: Map<string, string>): string[] {
    const requestSignalList: string[] = [];

    for (const selectedSignal of selectedSignals) {
      const requestSignal: string = selectedSignal + '@' + signalTypes[selectedSignal];
      requestSignalList.push(requestSignal);
    }
    return requestSignalList;
  }

  public getSelectedSignalsFromUserDefinedPage(selectedSignals: ChartSignalEntry[]): string [] {
    const signalNames: string[] = [];
    for (const selectedSignal of selectedSignals) {
      signalNames.push(selectedSignal.signalName);
    }
    return signalNames;
  }

  public getDataRequest(signalList: string[], startInterval: string, endInterval: string, unit: string, machineId: number, organizationId: number) {
    const requestData = {
      signals: signalList,
      startInterval: startInterval,
      endInterval: endInterval,
      unit: unit
    };
    return {
      machine: machineId,
      request: JSON.stringify(requestData),
      tenant: organizationId
    };
  }

  public getParsedData(values: DataVisualizationValues) {
    const signalData: ParsedSignalData = [];
    for (const value in values) {
      signalData.push({
        serieName: value,
        values: values[value]
      });
    }
    return signalData;
  }

  public aggregateData(originalData: ParsedSignalData, customBaseUnit: string) {
    const aggregationInterval = InteractiveDataAnalysisService.getAggregationInterval(customBaseUnit);

    const dataArray: AggregatedSignalData = {};
    for (const data of originalData) {
      const serieName = data.serieName;
      const rangeName = data.serieName + '_range';

      dataArray[serieName] = [];
      dataArray[rangeName] = [];

      if (aggregationInterval === -1) {
        this.aggregateDataInSeconds(data, dataArray, serieName, rangeName);
      } else {
        this.aggregateDataNotInSeconds(data, aggregationInterval, dataArray, serieName, rangeName);
      }
    }
    return dataArray;
  }

  public getStringDataRequest(selectedString: string, signalTypes: Map<string, string>, startInterval: string, endInterval: string, unit: string, machineId: number, organizationId: number) {
    const stringName: string = this.getStringName(selectedString);
    const requestStringData = {
      signals: [stringName + '@' + signalTypes[stringName]],
      startInterval: startInterval,
      endInterval: endInterval,
      unit: unit
    };

    return {
      machine: machineId,
      request: JSON.stringify(requestStringData),
      tenant: organizationId
    };
  }

  public getStringSignal(selectedStringSignal: string, stringSignals: SignalField[]): SignalField | null {
    for (const stringSignal of stringSignals) {
      if (stringSignal.id === selectedStringSignal) {
        return stringSignal;
      }
    }
    return null;
  }

  public getStringData(receivedStringData: any, selectedStringSignal: SignalField | null) {
    const emptyResult = {
      name: ChartsHelperService.NO_STRING,
      serieName: ChartsHelperService.NO_STRING,
      displayedText: this.NO_STRING_VALUE,
      values: []
    };

    if (!selectedStringSignal || !selectedStringSignal.name || selectedStringSignal.name === ChartsHelperService.NO_STRING) {
      return emptyResult;
    }
    let signalName: string;
    let displayedText: string;
    if (!selectedStringSignal.machineSignal) {
      signalName = selectedStringSignal.table + '.' + selectedStringSignal.name;
      displayedText = selectedStringSignal.table + '.' + selectedStringSignal.name;
    } else {
      signalName = selectedStringSignal.name;
      displayedText = selectedStringSignal.displayedText;
    }

    if (receivedStringData) {
      return this.setStringData(receivedStringData, signalName, displayedText);
    }
    return {
      serieName: signalName,
      displayedText: displayedText,
      values: []
    };
  }

  public getDate(event): number {
    const date = new Date(Number(event.date));

    date.setHours(Number(event.time.hour));
    date.setMinutes(Number(event.time.minute));
    date.setSeconds(0);
    return date.getTime();
  }

  public getFullDetailsOfSignal(signal: SignalField, signalsOrderedByConfigurationsType: SignalField[], interactiveDataAnalysisConfig: InteractiveDataAnalysisConfig): InteractiveDataAnalysisSignalChartProperties {
    const failsafeProperties: InteractiveDataAnalysisSignalChartProperties = new InteractiveDataAnalysisSignalChartProperties();
    failsafeProperties.customRange = false;
    failsafeProperties.minY = 0;
    failsafeProperties.maxY = 0;
    failsafeProperties.axis = InteractiveDataAnalysisService.getAxisType(signal, signalsOrderedByConfigurationsType);
    const signalConfigProperties: InteractiveDataAnalysisProperty = interactiveDataAnalysisConfig?.properties?.get(signal.id) as InteractiveDataAnalysisProperty;
    if (signalConfigProperties) {
      failsafeProperties.customRange = signalConfigProperties.customRange;
      failsafeProperties.minY = signalConfigProperties.minY;
      failsafeProperties.maxY = signalConfigProperties.maxY;
      failsafeProperties.colors.main = signalConfigProperties.color;
    }
    return failsafeProperties;
  }

  public generateIDADefaultSignalsVisibilitySettings(machineId, signals) {
    const key = 'interactiveDataAnalysis_signalsVisibility_' + machineId;
    const settings = {};
    for (const signal of signals) {
      if (signal) {
        settings[signal] = {
          isSignalVisible: true, isRangeVisible: false
        };
      }
    }
    this.storageService.writeValueToSessionStorage(key, JSON.stringify(settings));
  }

  getIntervals(intervalType: string) {
    let startInterval: Date = new Date();
    let endInterval: Date = new Date();
    let interval: number = 0;
    const now = new Date();
    switch (intervalType) {
      case IntervalTypes.CURRENT_DAY:
        endInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0));
        startInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0));
        interval = this.INTERVAL_OF_10;
        break;
      case IntervalTypes.YESTERDAY:
        endInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0));
        startInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 1, 0, 0, 0));
        interval = this.INTERVAL_OF_10;
        break;
      case IntervalTypes.LAST_7_DAYS:
        endInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0));
        startInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 7, 0, 0, 0));
        interval = this.INTERVAL_OF_60;
        break;
      case IntervalTypes.LAST_30_DAYS:
        endInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0));
        startInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 30, 0, 0, 0));
        interval = this.INTERVAL_OF_900;
        break;
      case IntervalTypes.CURRENT_MONTH:
        endInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0));
        startInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1, 0, 0, 0));
        interval = this.INTERVAL_OF_900;
        break;
      case IntervalTypes.CURRENT_YEAR:
        endInterval = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0));
        startInterval = new Date(Date.UTC(now.getFullYear(), 0, 1, 0, 0, 0));
        interval = this.INTERVAL_OF_3600;
        break;
    }

    startInterval.setMinutes(startInterval.getMinutes() + now.getTimezoneOffset());
    endInterval.setMinutes(endInterval.getMinutes() + now.getTimezoneOffset());
    return {
      endInterval, startInterval, interval
    };
  }

  public calculateCustomTimeAggregation(endDate: number, startDate: number, aggregationUnitsMap: Map<string, AggregationType[]>) {
    const diff = endDate - startDate;
    const hoursDiff = (diff / 1000) / 3600;

    let interval: number;
    let customBaseUnit: string;
    let aggregationUnits: AggregationType[];
    if (hoursDiff < this.HOURS) {
      aggregationUnits = this.getPossibleAggregationUnits(IntervalTypes.CUSTOM_HOURS, aggregationUnitsMap);
      customBaseUnit = this.getDefaultDisplayInterval(IntervalTypes.CUSTOM_HOURS, aggregationUnitsMap);
      interval = this.INTERVAL_OF_1;
    } else if (hoursDiff < this.DAY) {
      aggregationUnits = this.getPossibleAggregationUnits(IntervalTypes.CUSTOM_DAY, aggregationUnitsMap);
      customBaseUnit = this.getDefaultDisplayInterval(IntervalTypes.CURRENT_DAY, aggregationUnitsMap);
      interval = this.INTERVAL_OF_10;
    } else if (hoursDiff < this.DAYS_10) {
      aggregationUnits = this.getPossibleAggregationUnits(IntervalTypes.CUSTOM_DAYS, aggregationUnitsMap);
      customBaseUnit = this.getDefaultDisplayInterval(IntervalTypes.LAST_7_DAYS, aggregationUnitsMap);
      interval = this.INTERVAL_OF_60;
    } else if (hoursDiff < this.DAYS_120) {
      aggregationUnits = this.getPossibleAggregationUnits(IntervalTypes.CUSTOM_MONTHS, aggregationUnitsMap);
      customBaseUnit = this.getDefaultDisplayInterval(IntervalTypes.CURRENT_MONTH, aggregationUnitsMap);
      interval = this.INTERVAL_OF_900;
    } else {
      aggregationUnits = this.getPossibleAggregationUnits(IntervalTypes.CUSTOM_YEAR, aggregationUnitsMap);
      customBaseUnit = this.getDefaultDisplayInterval(IntervalTypes.CURRENT_YEAR, aggregationUnitsMap);
      interval = this.INTERVAL_OF_3600;
    }

    return {
      customBaseUnit, interval, aggregationUnits
    };
  }

  public getPossibleAggregationUnits(id: string, aggregationUnits: Map<string, AggregationType[]>) {
    const aggregationUnitArray = aggregationUnits.get(id);
    if (aggregationUnitArray !== undefined) {
      return aggregationUnitArray;
    }
    return [];
  }

  private aggregateDataInSeconds(data, dataArray: AggregatedSignalData, serieName: string, rangeName: string) {
    for (const value of data.values) {
      if (value && value.x) {
        dataArray[serieName].push([value.x * 1000, value.y]);
        dataArray[rangeName].push([value.x * 1000, value.min, value.max]);
      }
    }
  }

  private aggregateDataNotInSeconds(data, aggregationInterval: number, dataArray: AggregatedSignalData, serieName: string, rangeName: string) {
    const tmpMap = {};
    for (const value of data.values) {
      if (value && value.x) {
        const key = (Math.floor(value.x / aggregationInterval) * aggregationInterval).toString();
        let val = value.y;
        const min = value.min;
        const max = value.max;
        if (val === undefined) {
          continue;
        }
        if (!tmpMap.hasOwnProperty(key)) {
          tmpMap[key] = {
            y: val,
            c: 1,
            min: min,
            max: max
          };
          continue;
        }

        val = val ? val : 0;
        tmpMap[key].y = tmpMap[key].y ? tmpMap[key].y : 0;
        tmpMap[key].y += val;
        tmpMap[key].c++;

        if (tmpMap[key].min > min) {
          tmpMap[key].min = min;
        }
        if (tmpMap[key].max < max) {
          tmpMap[key].max = max;
        }
      }
    }

    for (const tmp in tmpMap) {
      if (tmpMap.hasOwnProperty(tmp)) {
        dataArray[serieName].push([Number(tmp) * 1000, tmpMap[tmp].y / tmpMap[tmp].c]);
        dataArray[rangeName].push([Number(tmp) * 1000, tmpMap[tmp].min, tmpMap[tmp].max]);
      }
    }
  }

  private setStringData(receivedStringData: any, selectedStringName: string, displayedText: string) {
    const receivedStringDataValues: any[] = [];
    for (const t in receivedStringData) {
      if (receivedStringData[t].stringValue !== 'Null') {
        const t1 = receivedStringData[t].timestampMin * 1000;
        const t2 = receivedStringData[t].timestampMax * 1000;
        receivedStringDataValues.push([t1, t2, receivedStringData[t].stringValue]);
      }
    }
    return {
      serieName: selectedStringName,
      displayedText: displayedText,
      values: receivedStringDataValues,
    };
  }

  private getStringName(selectedString: string): string {
    if (selectedString === '-1') {
      return '@' + this.NO_STRING_VALUE;
    }
    return selectedString;
  }
}
